import type { IAssetSource } from '@monofe/util';
import type { DetailedHTMLProps, SourceHTMLAttributes, UIEvent } from 'react';
import { log } from '../private';
import type { IImageProperties } from './Image.interface';
import { ImageLoadingStrategy } from './Image.interface';
import { StyleElem as StyleElement, classNameForElement, styleForElement } from './Image.style';

const DEFAULT_LOADING_STRATEGY = ImageLoadingStrategy.Lazy;
const LOADING_STRATEGY: Record<ImageLoadingStrategy, 'lazy' | 'eager'> = Object.freeze({
    [ImageLoadingStrategy.Undef]: 'lazy',
    [ImageLoadingStrategy.Lazy]: 'lazy',
    [ImageLoadingStrategy.Eager]: 'eager',
});

const sourceElement = (
    properties: IAssetSource,
): DetailedHTMLProps<SourceHTMLAttributes<HTMLSourceElement>, HTMLSourceElement> => (
    <source
        key={properties.src}
        type={properties.type}
        srcSet={properties.src}
        media={properties.media}
        width={properties.width}
        height={properties.height}
    />
);

const _hideElementOnError = (event: UIEvent<HTMLImageElement> & { target: HTMLImageElement }) => {
    log.error(`Failed to load image url: '${event.target.src}'`);
    event.target.style.opacity = '0';
};

const _showElementOnLoad = (event: UIEvent<HTMLImageElement> & { target: HTMLImageElement }) => {
    event.target.style.opacity = '1';
};

function Image(properties: Readonly<IImageProperties>): JSX.Element | null {
    const style = (element: StyleElement) => styleForElement(element, properties);
    const className = (element: StyleElement) => classNameForElement(element, properties);

    const onLoadCallback = () => {
        properties.onLoaded?.();
    };

    const fallbackImageAsset =
        properties.urls.length > 0 ? properties.urls[properties.urls.length - 1] : null;
    const sourceElementUrls = properties.urls.length > 0 ? properties.urls.slice(0, -1) : [];

    /* Nothing to render */
    if (properties.urls.length <= 0 || !fallbackImageAsset) {
        return null;
    }

    return (
        <picture
            className={`${properties.className ?? ''} ${className(StyleElement.Picture)}`}
            style={style(StyleElement.Picture)}
            onLoad={onLoadCallback}
        >
            {sourceElementUrls
                .filter(source => Boolean(source))
                .map(source => sourceElement(source))}
            <img
                src={fallbackImageAsset.src}
                width={fallbackImageAsset.width}
                height={fallbackImageAsset.height}
                onError={_hideElementOnError}
                onLoad={_showElementOnLoad}
                className={className(StyleElement.Image)}
                style={style(StyleElement.Image)}
                alt={properties.description ?? 'Image'}
                decoding="async"
                loading={LOADING_STRATEGY[properties.loading ?? DEFAULT_LOADING_STRATEGY]}
            />
            {properties.children}
        </picture>
    );
}

export default Image;
